import { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import { logActivity } from '@/lib/activityLog';
import { useSalesSettings } from '@/hooks/useSalesSettings';
import { DEFAULT_PAYMENT_METHODS } from '@/lib/salesSettings';
import { dayRange, round2, todayStr, type CashSummary } from '@/lib/cashSession';
import { withTimeout, chunk } from '@/lib/queryTimeout';
import {
  computeProfits,
  type ProfitProductMeta,
} from '@/lib/profits';
import type {
  CashMovement,
  CashMovementType,
  CashSession,
  PaymentBreakdownRow,
  Sale,
  SaleItem,
} from '@/lib/types';
import { saleCashPortion, paymentLabel } from '@/lib/types';

interface ProfitTotals {
  revenue: number;
  cost: number;
  profit: number;
  margin: number;
}

const EMPTY_PROFIT: ProfitTotals = { revenue: 0, cost: 0, profit: 0, margin: 0 };

const SESSION_SELECT = '*, branch:branches(name)';

export function useCashSession(periodDate: string) {
  const { user, profile, isAdmin } = useAuth();
  const { activeBranch, branches } = useBranch();
  const { settings } = useSalesSettings();

  const [session, setSession] = useState<CashSession | null>(null);
  const [sales, setSales] = useState<Sale[]>([]);
  const [movements, setMovements] = useState<CashMovement[]>([]);
  const [profitTotals, setProfitTotals] = useState<ProfitTotals>(EMPTY_PROFIT);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const branch = isAdmin ? activeBranch ?? branches[0] ?? null : activeBranch;
  const branchId = branch?.id ?? null;

  const userName =
    [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') ||
    profile?.username ||
    profile?.email ||
    'Usuario';
  const roleLabel = isAdmin ? 'Administrador' : 'Cajero';

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { startISO, endISO } = dayRange(periodDate);

      // 1. Ventas del usuario actual (nunca de otros cajeros).
      let salesQuery = supabase
        .from('sales')
        .select('*')
        .eq('cashier_id', user.id)
        .in('status', ['completed', 'partial_refund'])
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .order('created_at', { ascending: true });
      if (branchId) salesQuery = salesQuery.eq('branch_id', branchId);
      const { data: salesData, error: salesErr } = await withTimeout(salesQuery, 25000);
      if (salesErr) throw salesErr;

      // 2. Movimientos de caja del usuario actual en el período.
      let movQuery = supabase
        .from('cash_movements')
        .select('*')
        .eq('user_id', user.id)
        .gte('created_at', startISO)
        .lt('created_at', endISO)
        .order('created_at', { ascending: true });
      if (branchId) movQuery = movQuery.eq('branch_id', branchId);
      const { data: movData, error: movErr } = await withTimeout(movQuery, 25000);
      if (movErr) throw movErr;

      // 3. Cuadre existente para ese día (usuario + sucursal).
      let sesQuery = supabase
        .from('cash_sessions')
        .select(SESSION_SELECT)
        .eq('user_id', user.id)
        .eq('period_date', periodDate)
        .order('created_at', { ascending: false })
        .limit(1);
      if (branchId) sesQuery = sesQuery.eq('branch_id', branchId);
      const { data: sesData, error: sesErr } = await withTimeout(sesQuery, 25000);
      if (sesErr) throw sesErr;

      let currentSession = ((sesData as CashSession[]) ?? [])[0] ?? null;

      // 4. Si es el día de hoy, sin cuadre abierto, crear uno (estado "abierto").
      if (!currentSession && periodDate === todayStr() && branchId) {
        const { data: created, error: createErr } = await supabase
          .from('cash_sessions')
          .insert({
            user_id: user.id,
            user_name: userName,
            user_role: roleLabel,
            branch_id: branchId,
            period_date: periodDate,
            status: 'open',
          })
          .select(SESSION_SELECT)
          .maybeSingle();
        if (!createErr && created) {
          currentSession = created as CashSession;
          void logActivity({
            module: 'cash',
            action: 'cash_session_opened',
            description: `Caja abierta (${periodDate})`,
            targetType: 'cash_session',
            targetId: currentSession.id,
            branchId,
          });
        }
      }

      // 5. Rentabilidad real de esas ventas (usa el costo congelado en cada renglón).
      let totals: ProfitTotals = EMPTY_PROFIT;
      const saleList = (salesData as Sale[]) ?? [];
      if (saleList.length > 0) {
        const saleIds = saleList.map((s) => s.id);

        // Los renglones se piden en bloques para no generar consultas gigantes
        // cuando el día tiene muchas ventas (causa de lentitud al final del día).
        const itemRows: SaleItem[] = [];
        for (const block of chunk(saleIds, 100)) {
          const res = await withTimeout(
            supabase
              .from('sale_items')
              .select('sale_id, product_id, product_name, quantity, subtotal, unit_cost, returned_quantity')
              .in('sale_id', block),
            25000
          );
          if (res.error) throw res.error;
          itemRows.push(...((res.data as unknown as SaleItem[]) ?? []));
        }

        // Solo los productos que realmente aparecen en esas ventas, no todo el
        // catálogo: antes se descargaba la tabla completa en cada carga.
        const productIds = [
          ...new Set(itemRows.map((i) => i.product_id).filter(Boolean)),
        ] as string[];
        let productMeta: ProfitProductMeta[] = [];
        if (productIds.length > 0) {
          for (const block of chunk(productIds, 100)) {
            const res = await withTimeout(
              supabase
                .from('products')
                .select('id, sku, category_id, purchase_price, sale_price')
                .in('id', block),
              25000
            );
            if (res.error) throw res.error;
            productMeta.push(...((res.data as unknown as ProfitProductMeta[]) ?? []));
          }
        }

        const built = computeProfits(saleList, itemRows, productMeta, []);
        totals = {
          revenue: built.summary.revenue,
          cost: built.summary.cost,
          profit: built.summary.profit,
          margin: built.summary.margin,
        };
      }

      setSales(saleList);
      setMovements((movData as CashMovement[]) ?? []);
      setProfitTotals(totals);
      setSession(currentSession);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cargar el cuadre');
    } finally {
      setLoading(false);
    }
  }, [user?.id, branchId, periodDate, userName, roleLabel]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo<CashSummary>(() => {
    const salesCount = sales.length;
    const subtotal = round2(sales.reduce((s, x) => s + Number(x.subtotal || 0), 0));
    const discount = round2(sales.reduce((s, x) => s + Number(x.discount || 0), 0));
    const tax = round2(sales.reduce((s, x) => s + Number(x.tax || 0), 0));
    const total = round2(sales.reduce((s, x) => s + Number(x.total || 0), 0));

    const cashSales = round2(
      sales.reduce((s, x) => s + saleCashPortion(x), 0)
    );

    const cashIn = round2(
      movements.filter((m) => m.type === 'entrada').reduce((s, m) => s + Number(m.amount || 0), 0)
    );
    const cashOut = round2(
      movements.filter((m) => m.type === 'retiro').reduce((s, m) => s + Number(m.amount || 0), 0)
    );
    const returnsTotal = round2(
      movements
        .filter((m) => m.type === 'devolucion')
        .reduce((s, m) => s + Number(m.amount || 0), 0) +
        sales.reduce((s, x) => s + Number(x.refund_total || 0), 0)
    );

    const cashExpected = round2(cashSales + cashIn - cashOut - returnsTotal);

    // Desglose por método de pago (según los métodos realmente usados).
    const methods = settings?.payment_methods ?? DEFAULT_PAYMENT_METHODS;
    const map = new Map<string, PaymentBreakdownRow>();
    methods.forEach((m) =>
      map.set(m.id, { method: m.id, label: m.name, count: 0, total: 0 })
    );
    sales.forEach((s) => {
      const key = s.payment_method || 'other';
      const row = map.get(key);
      if (row) {
        row.count += 1;
        row.total = round2(row.total + Number(s.total || 0));
      } else {
        map.set(key, {
          method: key,
          label: paymentLabel(key),
          count: 1,
          total: round2(Number(s.total || 0)),
        });
      }
    });
    const breakdown = [...map.values()].filter((r) => r.count > 0 || methods.some((m) => m.id === r.method));

    return {
      salesCount,
      subtotal,
      discount,
      tax,
      total,
      returnsTotal,
      net: round2(total - returnsTotal),
      cashSales,
      cashIn,
      cashOut,
      cashExpected,
      breakdown,
      revenue: profitTotals.revenue,
      cost: profitTotals.cost,
      profit: profitTotals.profit,
      margin: profitTotals.margin,
    };
  }, [sales, movements, settings, profitTotals]);

  const addMovement = useCallback(
    async (type: CashMovementType, amount: number, note: string) => {
      if (!user?.id) return { error: 'Sesión no válida.' };
      if (!Number.isFinite(amount) || amount <= 0) return { error: 'El monto debe ser mayor que cero.' };
      const { data, error: insErr } = await supabase
        .from('cash_movements')
        .insert({
          user_id: user.id,
          branch_id: branchId,
          type,
          amount: round2(amount),
          note: note.trim() || null,
        })
        .select('*')
        .maybeSingle();
      if (insErr) return { error: insErr.message };
      setMovements((prev) => [...prev, data as CashMovement]);
      void logActivity({
        module: 'cash',
        action: 'cash_movement_added',
        description: `Movimiento de caja (${type}): ${round2(amount)}`,
        targetType: 'cash_movement',
        targetId: (data as CashMovement)?.id,
        metadata: { type, amount: round2(amount), note: note.trim() || null },
        branchId,
      });
      return { error: null };
    },
    [user?.id, branchId]
  );

  const removeMovement = useCallback(async (id: string) => {
    const { error: delErr } = await supabase.from('cash_movements').delete().eq('id', id);
    if (delErr) return { error: delErr.message };
    setMovements((prev) => prev.filter((m) => m.id !== id));
    return { error: null };
  }, []);

  const closeSession = useCallback(
    async (cashCounted: number, observations: string) => {
      if (!user?.id || !branchId) return { error: 'Selecciona una sucursal para cerrar el cuadre.' };
      if (!Number.isFinite(cashCounted) || cashCounted < 0) {
        return { error: 'El efectivo contado no es válido.' };
      }
      const difference = round2(cashCounted - summary.cashExpected);
      const payload = {
        user_id: user.id,
        user_name: userName,
        user_role: roleLabel,
        branch_id: branchId,
        period_date: periodDate,
        status: 'closed',
        closed_at: new Date().toISOString(),
        sales_count: summary.salesCount,
        subtotal: summary.subtotal,
        discount: summary.discount,
        tax: summary.tax,
        total: summary.total,
        returns_total: summary.returnsTotal,
        cash_sales: summary.cashSales,
        cash_in: summary.cashIn,
        cash_out: summary.cashOut,
        cash_expected: summary.cashExpected,
        cash_counted: round2(cashCounted),
        difference,
        revenue: summary.revenue,
        cost_total: summary.cost,
        profit: summary.profit,
        margin: summary.margin,
        payment_breakdown: summary.breakdown,
        observations: observations.trim() || null,
        updated_at: new Date().toISOString(),
      };

      const request = session
        ? supabase.from('cash_sessions').update(payload).eq('id', session.id).select(SESSION_SELECT).maybeSingle()
        : supabase.from('cash_sessions').insert(payload).select(SESSION_SELECT).maybeSingle();
      const { data, error: saveErr } = await request;
      if (saveErr) return { error: saveErr.message };
      setSession(data as CashSession);
      void logActivity({
        module: 'cash',
        action: 'cash_session_closed',
        description: `Cuadre cerrado (${periodDate})`,
        targetType: 'cash_session',
        targetId: session?.id ?? (data as CashSession)?.id,
        metadata: {
          total: summary.total,
          cash_expected: summary.cashExpected,
          cash_counted: round2(cashCounted),
          difference,
        },
        branchId,
      });
      return { error: null, session: data as CashSession };
    },
    [user?.id, branchId, summary, userName, roleLabel, periodDate, session]
  );

  return {
    session,
    sales,
    movements,
    summary,
    loading,
    error,
    branch,
    branchId,
    userName,
    roleLabel,
    canUse: Boolean(user?.id && branchId),
    isClosed: session?.status === 'closed',
    reload: load,
    addMovement,
    removeMovement,
    closeSession,
  };
}